import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { courierAdvantages, courierCases, courierFaqs } from "@/mocks/courier";
import { siteStats } from "@/mocks/site";

export default function Courier() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  usePageMeta(
    "快递服务 - 美味的快递 | 同城急送与快递查询",
    "美味的快递提供覆盖全城的同城急送服务，最快 30 分钟送达，生鲜、药品、文件均可寄送，支持实时查询与保价理赔，寄件更省心。",
    "美味的快递,快递服务,同城急送,快递查询,寄件,同城配送,快速快递"
  );

  return (
    <div className="bg-background-50">
      <section className="relative h-[380px] w-full overflow-hidden md:h-[480px]">
        <img
          src="https://readdy.ai/api/search-image?query=delivery%20couriers%20on%20scooters%20with%20insulated%20bags%20moving%20through%20a%20warm%20sunlit%20city%20avenue%20at%20golden%20hour%2C%20motion%20blur%2C%20orange%20amber%20and%20brown%20tones%2C%20cinematic%20editorial%20photography%2C%20dynamic%20and%20energetic%2C%20high%20detail&width=1600&height=900&seq=courier-hero&orientation=landscape"
          alt="美味的快递同城急送骑手"
          title="美味的快递 - 同城急送服务"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
            <div className="max-w-2xl animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-4 py-1.5 text-xs font-bold text-background-50">
                <i className="ri-e-bike-2-fill" />
                同城急送 · 全城覆盖
              </span>
              <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-white md:text-5xl">
                想寄就寄，最快 30 分钟送到
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
                自建配送网络覆盖全城 120+ 片区，生鲜、药品、文件都能寄。在线下单、实时追踪、保价理赔，一站式搞定。
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/courier-send"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 py-3.5 text-sm font-bold text-background-50 transition hover:bg-accent-600"
                >
                  <i className="ri-send-plane-fill" />
                  立即寄件
                </Link>
                <Link
                  to="/courier-track"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/60 bg-white/10 px-7 py-3.5 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20"
                >
                  <i className="ri-search-eye-line" />
                  查询快递
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {siteStats.map((s) => (
            <div key={s.label} className="rounded-lg border border-background-200 bg-background-100 p-5 text-center">
              <p className="font-heading text-xl font-extrabold text-primary-600 md:text-2xl">{s.value}</p>
              <p className="mt-1 text-xs text-foreground-600 md:text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <h2 className="text-center font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">快捷功能入口</h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-sm text-foreground-500 md:text-base">
            两步入完成寄件与查询，随时随地掌握你的包裹动态。
          </p>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <Link
              to="/courier-send"
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-8 transition hover:-translate-y-1 hover:border-accent-300"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-send-plane-fill text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-extrabold text-foreground-950">快递寄件</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                填写寄件人与收件人信息、包裹内容与上门时间，下单后骑手就近接单，最快 30 分钟上门取件。
              </p>
              <span className="mt-5 flex items-center gap-1.5 text-sm font-bold text-accent-600">
                去填写寄件信息
                <i className="ri-arrow-right-line transition group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/courier-track"
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-8 transition hover:-translate-y-1 hover:border-primary-300"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-extrabold text-foreground-950">快递查询</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                输入运单号即可查看从揽收、运输到签收的完整轨迹，时间与节点清晰可查，随时掌握配送进度。
              </p>
              <span className="mt-5 flex items-center gap-1.5 text-sm font-bold text-primary-600">
                去查询物流轨迹
                <i className="ri-arrow-right-line transition group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3.5 py-1.5 text-xs font-bold text-accent-700">
            <i className="ri-thumb-up-line" />
            为什么选择我们
          </span>
          <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            速度、覆盖与保障，一个都不少
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {courierAdvantages.map((a) => (
            <div key={a.title} className="rounded-lg border border-background-200 bg-background-50 p-6 transition hover:border-accent-300">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                <i className={`${a.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{a.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3.5 py-1.5 text-xs font-bold text-primary-700">
                <i className="ri-question-line" />
                常见问题
              </span>
              <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                寄快递前，你可能想先了解这些
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                还有疑问？欢迎拨打 400-880-1234 或前往「联系我们」留言，客服 7×24 小时为你解答。
              </p>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-primary-300 bg-background-50 px-5 py-2.5 text-sm font-bold text-primary-700 transition hover:bg-primary-50"
              >
                <i className="ri-customer-service-2-line" />
                联系客服
              </Link>
            </div>

            <div className="flex flex-col gap-3">
              {courierFaqs.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={f.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                    >
                      <span className="font-heading text-sm font-bold text-foreground-950 md:text-base">{f.q}</span>
                      <i className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} shrink-0 text-lg text-primary-600`} />
                    </button>
                    {isOpen && (
                      <p className="animate-fade-in border-t border-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-600">
                        {f.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-8 md:py-20">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-bold text-secondary-900">
            <i className="ri-chat-smile-3-line" />
            用户故事
          </span>
          <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            他们这样评价美味的快递
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {courierCases.map((c) => (
            <article key={c.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
              <i className="ri-double-quotes-l text-2xl text-accent-400" />
              <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{c.content}</p>
              <div className="mt-5 flex items-center gap-3 border-t border-background-100 pt-4">
                <img
                  src={c.avatar}
                  alt={`${c.name} 的用户头像`}
                  title={`${c.name} - 美味的快递用户`}
                  className="h-11 w-11 rounded-full object-cover"
                />
                <div>
                  <p className="font-heading text-sm font-bold text-foreground-950">{c.name}</p>
                  <p className="text-xs text-foreground-500">{c.role}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}